/**
 * Region mapping: maps ISO-2 country codes to a region key,
 * plus defines which countries are "neighbours" or in the same region.
 */

const REGION_MAP: Record<string, string> = {
  // East Africa
  SS: 'east-africa', ET: 'east-africa', KE: 'east-africa', UG: 'east-africa',
  SO: 'east-africa', TZ: 'east-africa', RW: 'east-africa', BI: 'east-africa',
  ER: 'east-africa', DJ: 'east-africa', SD: 'east-africa',
  // West Africa
  NG: 'west-africa', GH: 'west-africa', SN: 'west-africa', CI: 'west-africa',
  ML: 'west-africa', BF: 'west-africa', NE: 'west-africa', GM: 'west-africa',
  GN: 'west-africa', SL: 'west-africa', LR: 'west-africa', TG: 'west-africa',
  BJ: 'west-africa', MR: 'west-africa', GW: 'west-africa', CV: 'west-africa',
  // Central Africa
  CD: 'central-africa', CG: 'central-africa', CM: 'central-africa', CF: 'central-africa',
  TD: 'central-africa', GA: 'central-africa', GQ: 'central-africa', ST: 'central-africa',
  // Southern Africa
  ZA: 'southern-africa', ZW: 'southern-africa', MZ: 'southern-africa', MW: 'southern-africa',
  ZM: 'southern-africa', BW: 'southern-africa', NA: 'southern-africa', SZ: 'southern-africa',
  LS: 'southern-africa', MG: 'southern-africa', AO: 'southern-africa',
  // North Africa / Middle East
  EG: 'north-africa-me', LY: 'north-africa-me', TN: 'north-africa-me', DZ: 'north-africa-me',
  MA: 'north-africa-me', SA: 'north-africa-me', AE: 'north-africa-me', IQ: 'north-africa-me',
  JO: 'north-africa-me', LB: 'north-africa-me', SY: 'north-africa-me', YE: 'north-africa-me',
  OM: 'north-africa-me', QA: 'north-africa-me', BH: 'north-africa-me', KW: 'north-africa-me',
  PS: 'north-africa-me', IL: 'north-africa-me',
  // South Asia
  IN: 'south-asia', PK: 'south-asia', BD: 'south-asia', LK: 'south-asia',
  NP: 'south-asia', AF: 'south-asia', MV: 'south-asia', BT: 'south-asia',
  // Southeast Asia
  PH: 'southeast-asia', VN: 'southeast-asia', TH: 'southeast-asia', MY: 'southeast-asia',
  ID: 'southeast-asia', SG: 'southeast-asia', MM: 'southeast-asia', KH: 'southeast-asia',
  LA: 'southeast-asia', BN: 'southeast-asia', TL: 'southeast-asia',
  // East Asia
  CN: 'east-asia', JP: 'east-asia', KR: 'east-asia', TW: 'east-asia',
  HK: 'east-asia', MO: 'east-asia', MN: 'east-asia',
  // Europe
  GB: 'europe', DE: 'europe', FR: 'europe', IT: 'europe', ES: 'europe',
  PT: 'europe', NL: 'europe', BE: 'europe', AT: 'europe', CH: 'europe',
  SE: 'europe', NO: 'europe', DK: 'europe', FI: 'europe', IE: 'europe',
  PL: 'europe', CZ: 'europe', RO: 'europe', HU: 'europe', GR: 'europe',
  HR: 'europe', BG: 'europe', SK: 'europe', SI: 'europe', LT: 'europe',
  LV: 'europe', EE: 'europe', RS: 'europe', BA: 'europe', AL: 'europe',
  MK: 'europe', ME: 'europe', XK: 'europe', UA: 'europe', MD: 'europe',
  BY: 'europe', RU: 'europe', TR: 'europe', CY: 'europe', MT: 'europe',
  LU: 'europe', IS: 'europe',
  // Oceania
  AU: 'oceania', NZ: 'oceania', FJ: 'oceania', PG: 'oceania', WS: 'oceania',
  TO: 'oceania', VU: 'oceania', SB: 'oceania',
  // North America
  US: 'north-america', CA: 'north-america', MX: 'north-america',
  // Central America / Caribbean
  GT: 'central-america', HN: 'central-america', SV: 'central-america', NI: 'central-america',
  CR: 'central-america', PA: 'central-america', CU: 'central-america', JM: 'central-america',
  HT: 'central-america', DO: 'central-america', TT: 'central-america', BB: 'central-america',
  BS: 'central-america', BZ: 'central-america',
  // South America
  BR: 'south-america', AR: 'south-america', CO: 'south-america', CL: 'south-america',
  PE: 'south-america', VE: 'south-america', EC: 'south-america', BO: 'south-america',
  PY: 'south-america', UY: 'south-america', GY: 'south-america', SR: 'south-america',
  // Central Asia
  UZ: 'central-asia', KZ: 'central-asia', TJ: 'central-asia', KG: 'central-asia',
  TM: 'central-asia', IR: 'central-asia',
};

export function getRegion(iso2: string): string {
  return REGION_MAP[iso2.toUpperCase()] || 'other';
}

/**
 * Given an ISO-2 code, return all other codes in the same region.
 */
export function getRegionalCountryCodes(iso2: string): string[] {
  const region = getRegion(iso2);
  if (region === 'other') return [];
  return Object.entries(REGION_MAP)
    .filter(([code, r]) => r === region && code !== iso2.toUpperCase())
    .map(([code]) => code);
}
